import React, { useState, useEffect } from "react";
import { Typography, Box, Skeleton, ToggleButtonGroup, ToggleButton } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { getActivitiesStats, DashboardFilters, TimeSeriesData } from "../../../services/api/dashboard/dashboardApi";

interface TimeSeriesChartProps {
  filters: DashboardFilters;
}

export default function TimeSeriesChart({
  filters,
}: TimeSeriesChartProps) {
  // Estado local para granularidade e dados
  const [granularity, setGranularity] = useState<"day" | "week" | "month">("day");
  const [data, setData] = useState<TimeSeriesData[]>([]);
  const [loading, setLoading] = useState(false);

  // Buscar dados quando filtros ou granularidade mudam
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await getActivitiesStats({
          ...filters,
          granularity,
        });
        setData(response.timeSeries);
      } catch (error) {
        console.error("Erro ao buscar dados da série temporal:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    if (filters.startDate && filters.endDate) {
      fetchData();
    }
  }, [filters, granularity]);
  // Função para formatar datas baseado na granularidade
  const formatDate = (dateStr: string) => {
    try {
      const date = parseISO(dateStr);
      
      switch (granularity) {
        case "day":
          return format(date, "dd/MM", { locale: ptBR });
        case "week":
          return format(date, "dd/MM", { locale: ptBR });
        case "month":
          return format(date, "MMM/yy", { locale: ptBR });
        default:
          return format(date, "dd/MM", { locale: ptBR });
      }
    } catch {
      return dateStr;
    }
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: 1,
            p: 1.5,
            boxShadow: 2,
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            {formatDate(payload[0].payload.date)}
          </Typography>
          <Typography variant="body2" color="primary">
            Atividades: {payload[0].value}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width="40%" height={32} />
        <Skeleton variant="rectangular" height={300} sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Seletor de Granularidade */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <ToggleButtonGroup
          value={granularity}
          exclusive
          onChange={(_, newValue) => {
            if (newValue !== null) {
              setGranularity(newValue);
            }
          }}
          size="small"
        >
          <ToggleButton value="day">
            Diário
          </ToggleButton>
          <ToggleButton value="week">
            Semanal
          </ToggleButton>
          <ToggleButton value="month">
            Mensal
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ width: "100%", height: 350 }}>
        <ResponsiveContainer>
          <LineChart
            data={data || []}
            margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              style={{ fontSize: 12 }}
            />
            <YAxis style={{ fontSize: 12 }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line
              type="monotone"
              dataKey="count"
              stroke="#1976d2"
              strokeWidth={2}
              name="Quantidade"
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

